/**
 * Domain Defaults — Single Source of Truth
 * 
 * Computed ONCE per page load. All domain-driven behavior reads from here.
 * 
 * ## DOMAIN CONTRACT
 * 
 * tributario.com.br:
 *   - ALWAYS initializes as pt-BR locale + Brazil location + Tributário branding
 *   - Domain overrides DB account settings during init (only user_selection beats domain)
 *   - Users CAN change locale/location via selectors
 *   - If user moves away from Brazil+PT → DomainEnforcer redirects to tax.network
 *   - Homepage root "/" materializes as Brazil page (not global mode)
 * 
 * tax.network:
 *   - DEFAULTS to English + International + Tax.Network branding
 *   - Users CAN change locale/location freely
 *   - If user selects Brazil+PT via selectors → DomainEnforcer redirects to tributario.com.br
 * 
 * ## INIT PRIORITY CONTRACT
 * 
 * On tributario.com.br:
 *   1. User explicit selection (localStorage source=user_selection) → USE IT
 *   2. Domain default (pt-BR + Brazil) → USE IT (overrides account/IP/browser)
 * 
 * On tax.network / dev:
 *   1. User explicit selection (localStorage source=user_selection) → USE IT
 *   2. Account DB settings (authenticated user) → USE IT
 *   3. Stored session / browser / IP → USE IT
 *   4. English + International → FALLBACK
 * 
 * ## BRANDING
 * Brand is DOMAIN-LOCKED on tributario.com.br → always Tributário.
 * On tax.network / dev, brand is REACTIVE to locale + location:
 *   - locale=PT + location=Brazil → Tributário brand
 *   - anything else → Tax.Network brand
 * Domain-locking prevents logo flicker during the async redirect window.
 *
 * ## CROSS-DOMAIN ENFORCEMENT
 * DomainEnforcer.tsx watches locale + location reactively.
 * When they mismatch the current domain, it redirects with session transfer.
 * Skipped on dev domains (localhost, lovable.app, lovable.dev).
 */

import { isOnTributarioDomain, isOnTaxNetworkDomain, isDevDomain, BRAZIL_LOCATION_ID } from '@/constants/branding';
import { INTERNATIONAL_LOCATION_ID } from '@/constants/config';
import type { BrandId } from '@/config/brand';

/**
 * Stable domain detection — computed once at module load time.
 * Safe for SSR guards: returns false when window is undefined.
 */
const _isTributario = typeof window !== 'undefined' && isOnTributarioDomain();
const _isTaxNetwork = typeof window !== 'undefined' && isOnTaxNetworkDomain();
const _isDev = typeof window !== 'undefined' && isDevDomain();

export const DOMAIN_DEFAULTS = {
  /** True when hostname is tributario.com.br or a subdomain */
  isTributario: _isTributario,

  /** True when hostname is tax.network */
  isTaxNetwork: _isTaxNetwork,

  /** True for localhost / lovable.app / lovable.dev */
  isDev: _isDev,

  /** Default locale for first-time visitors on this domain (null = use detection chain) */
  defaultLocale: _isTributario ? 'pt-BR' : null,

  /** Default location_id for first-time visitors on this domain */
  defaultLocationId: _isTributario ? BRAZIL_LOCATION_ID : INTERNATIONAL_LOCATION_ID,

  /** Default brand for this domain */
  defaultBrandId: (_isTributario ? 'tributario' : 'tax_network') as BrandId,
} as const;
